import { FC, useEffect, useRef } from 'react'
import { useField, useFormikContext } from 'formik'
import { Box, Typography } from '@mui/material'
import { useTranslation } from '@app/lib/lang'
import { isArray, isGuid } from '@app/lib/utils'
import { CustomTextProps } from '../CustomTextField'
import { isIPv4 } from '@app/lib/helpers'
import theme from '@app/lib/materialui/theme'

const isMAC = (value: string) => {
	return /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(value)
}

const emailRegex =
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}$/

// yup
// 		.string()
// 		.min(8, 'passwordTooShort')
// 		.matches(/^(?=.*[a-z])/, 'passwordMustContainOneLowercase')
// 		.matches(/^(?=.*[A-Z])/, 'passwordMustContainOneUppercase')
// 		.matches(/^(?=.*[0-9])/, 'passwordMustContainOneNumber'),
export interface CustomFormGroupProps extends Pick<CustomTextProps, 'type'> {
	name: string
	label?: string
	fieldTranslatePrefix?: string
	required?: boolean
	validateFunction?: (value: string) => Promise<string | undefined>
	existingValues?: any[]
	children?: React.ReactNode
}

export const CustomFormGroup: FC<CustomFormGroupProps> = ({
	children,
	label,
	name,
	fieldTranslatePrefix,
	required,
	type,
	validateFunction,
	existingValues,
}) => {
	const errorRef = useRef<HTMLDivElement>(null)
	const [, meta] = useField({
		name,
		required,
		validate: async (value) => {
			if (validateFunction) {
				const error = await validateFunction(value)
				return error
			}

			if (value && type === 'email' && !emailRegex.test(value)) {
				return 'email'
			}

			if (value && type === 'password') {
				if (typeof value !== 'string' || value.length < 8) {
					return 'passwordTooShort'
				}
				if (!/(?=.*[a-z])/.test(value)) {
					return 'passwordMustContainOneLowercase'
				}
				if (!/(?=.*[A-Z])/.test(value)) {
					return 'passwordMustContainOneUppercase'
				}
				if (!/(?=.*[0-9])/.test(value)) {
					return 'passwordMustContainOneNumber'
				}
			}

			// if (value && type === 'uuid' && !isGuid(value)) {
			if (
				value &&
				type === 'uuid' &&
				!/^[0-9a-f]{32}$/i.test(value) &&
				!isGuid(value)
			) {
				return 'invalidUuid'
			}

			if (value && type === 'ipv4' && !isIPv4(value)) {
				return 'invalidIpv4'
			}

			if (value && type === 'mac' && !isMAC(value)) {
				return 'invalidMac'
			}

			if (
				required &&
				(value === undefined ||
					value === null ||
					value === '' ||
					(isArray(value) && !(value as any[]).length))
			) {
				return 'required'
			}

			if (existingValues && existingValues.length > 0) {
				const exists = existingValues.includes(value)
				if (exists) {
					if (name === 'templateKey') {
						return 'templateKeyAlreadyExists'
					} else {
						return 'valueAlreadyExists'
					}
				}
			}

			return
		},
	})
	const { submitCount } = useFormikContext()
	const { t } = useTranslation()

	let errorMessage = ''
	let labelTranslate: string

	if (label !== undefined) {
		labelTranslate = label
	} else {
		labelTranslate = fieldTranslatePrefix
			? `${fieldTranslatePrefix}.${name}`
			: name
	}

	if (meta.error && (meta.touched || submitCount > 0)) {
		errorMessage = t(`shared.validation.${meta.error}`, {
			field: t(labelTranslate),
		})
	}

	useEffect(() => {
		if (errorMessage && errorRef.current) {
			setTimeout(() => {
				errorRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				})
			}, 0)
		}
	}, [errorMessage])

	return (
		<>
			{children}
			{errorMessage && (
				<Box ref={errorRef} mt={1}>
					<Typography
						color={theme.palette.error.main}
						id={`${name}-error`}
					>
						{errorMessage}
					</Typography>
				</Box>
			)}
		</>
	)
}
